import { useState, useEffect } from 'react'
import { Save, RotateCcw, Server, Box, ChevronDown, ChevronRight } from 'lucide-react'
import { listPlatformSettings, updatePlatformSettings, resetPlatformSettings } from '../../lib/api'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'

interface FieldConfig {
  key: string
  label: string
  type: 'text' | 'number' | 'boolean' | 'select'
  options?: { value: string; label: string }[]
  placeholder?: string
  description?: string
}

interface CategoryConfig {
  key: string
  label: string
  icon: React.ElementType
  description: string
  fields: FieldConfig[]
}

const CATEGORIES: CategoryConfig[] = [
  {
    key: 'workspace_resources',
    label: 'Workspace Resources',
    icon: Server,
    description: 'Default resource limits for user workspaces',
    fields: [
      { key: 'cpu_cores', label: 'CPU Cores', type: 'number', placeholder: '2', description: 'Number of CPU cores (e.g., 1, 2, 4)' },
      { key: 'cpu_limit', label: 'CPU Limit', type: 'text', placeholder: '2000m', description: 'Kubernetes CPU limit (e.g., 500m, 1000m, 2000m)' },
      { key: 'memory_mb', label: 'Memory (MB)', type: 'number', placeholder: '4096', description: 'RAM in megabytes (e.g., 2048, 4096, 8192)' },
      { key: 'memory_limit', label: 'Memory Limit', type: 'text', placeholder: '4Gi', description: 'Kubernetes memory limit (e.g., 2Gi, 4Gi, 8Gi)' },
      { key: 'disk_gb', label: 'Disk Size (GB)', type: 'number', placeholder: '20', description: 'Persistent volume size in GB' },
      { key: 'gpu_enabled', label: 'GPU Access', type: 'boolean', description: 'Allow GPU access for workspaces' },
      { key: 'gpu_count', label: 'GPU Count', type: 'number', placeholder: '0', description: 'Number of GPUs to allocate' },
      { key: 'network_bandwidth', label: 'Network Bandwidth', type: 'select', options: [
        { value: 'standard', label: 'Standard (100 Mbps)' },
        { value: 'high', label: 'High (1 Gbps)' },
        { value: 'unlimited', label: 'Unlimited' },
      ]},
      { key: 'idle_timeout_minutes', label: 'Idle Timeout (min)', type: 'number', placeholder: '30', description: 'Auto-stop after idle minutes (0 = never)' },
      { key: 'max_runtime_hours', label: 'Max Runtime (hours)', type: 'number', placeholder: '24', description: 'Maximum runtime before auto-stop (0 = unlimited)' },
    ],
  },
  {
    key: 'app_resources',
    label: 'App Resources',
    icon: Box,
    description: 'Default resource limits for deployed application containers',
    fields: [
      { key: 'cpu_request', label: 'CPU Request', type: 'text', placeholder: '100m', description: 'Minimum CPU guarantee (e.g., 100m, 250m, 500m)' },
      { key: 'cpu_limit', label: 'CPU Limit', type: 'text', placeholder: '500m', description: 'Maximum CPU allowed (e.g., 500m, 1000m)' },
      { key: 'memory_request', label: 'Memory Request', type: 'text', placeholder: '256Mi', description: 'Minimum memory guarantee (e.g., 128Mi, 256Mi, 512Mi)' },
      { key: 'memory_limit', label: 'Memory Limit', type: 'text', placeholder: '512Mi', description: 'Maximum memory allowed (e.g., 512Mi, 1Gi)' },
      { key: 'replicas_min', label: 'Min Replicas', type: 'number', placeholder: '1', description: 'Minimum number of running instances' },
      { key: 'replicas_max', label: 'Max Replicas', type: 'number', placeholder: '3', description: 'Maximum replicas for auto-scaling' },
      { key: 'disk_gb', label: 'Ephemeral Storage (GB)', type: 'number', placeholder: '1', description: 'Ephemeral disk space in GB' },
      { key: 'port_range_start', label: 'Port Range Start', type: 'number', placeholder: '3000', description: 'Default app port' },
      { key: 'health_check_enabled', label: 'Health Checks', type: 'boolean', description: 'Enable liveness/readiness probes' },
      { key: 'auto_restart', label: 'Auto Restart', type: 'boolean', description: 'Automatically restart on crash' },
      { key: 'log_retention_days', label: 'Log Retention (days)', type: 'number', placeholder: '7', description: 'Days to keep application logs' },
    ],
  },
]

export default function AdminResourceLimits() {
  const [settings, setSettings] = useState<Record<string, Record<string, unknown>>>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(['workspace_resources']))

  useEffect(() => {
    loadSettings()
  }, [])

  async function loadSettings() {
    try {
      const data = await listPlatformSettings()
      const settingsMap: Record<string, Record<string, unknown>> = {}
      data.forEach(s => {
        if (s.category === 'workspace_resources' || s.category === 'app_resources') {
          settingsMap[s.category] = s.settings as Record<string, unknown>
        }
      })
      setSettings(settingsMap)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load settings')
    } finally {
      setLoading(false)
    }
  }

  function getValue(category: string, key: string): unknown {
    return settings[category]?.[key]
  }

  function setValue(category: string, key: string, value: unknown) {
    setSettings(prev => ({
      ...prev,
      [category]: { ...prev[category], [key]: value }
    }))
  }

  async function handleSave(category: string) {
    setSaving(category)
    setError(null)
    setSuccess(null)

    try {
      await updatePlatformSettings(category, settings[category] || {})
      setSuccess(`${category === 'workspace_resources' ? 'Workspace' : 'App'} resource limits saved`)
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings')
    } finally {
      setSaving(null)
    }
  }

  async function handleReset(category: string) {
    setSaving(category)
    setError(null)

    try {
      const result = await resetPlatformSettings(category)
      setSettings(prev => ({ ...prev, [category]: result.settings as Record<string, unknown> }))
      setSuccess(`${category === 'workspace_resources' ? 'Workspace' : 'App'} resource limits reset to defaults`)
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset settings')
    } finally {
      setSaving(null)
    }
  }

  function toggleCategory(category: string) {
    setExpandedCategories(prev => {
      const next = new Set(prev)
      if (next.has(category)) {
        next.delete(category)
      } else {
        next.add(category)
      }
      return next
    })
  }

  function renderField(category: string, field: FieldConfig) {
    const value = getValue(category, field.key)

    return (
      <div key={field.key} className="space-y-1">
        <label className="block text-sm font-medium">{field.label}</label>
        {field.description && (
          <p className="text-xs text-muted-foreground">{field.description}</p>
        )}
        {field.type === 'text' && (
          <input
            type="text"
            value={(value as string) || ''}
            onChange={(e) => setValue(category, field.key, e.target.value)}
            placeholder={field.placeholder}
            className="w-full px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
          />
        )}
        {field.type === 'number' && (
          <input
            type="number"
            value={(value as number) ?? ''}
            onChange={(e) => setValue(category, field.key, e.target.value ? parseInt(e.target.value) : undefined)}
            placeholder={field.placeholder}
            className="w-full px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
          />
        )}
        {field.type === 'boolean' && (
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={value as boolean ?? false}
              onChange={(e) => setValue(category, field.key, e.target.checked)}
              className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
            />
            <span className="text-sm text-muted-foreground">Enabled</span>
          </label>
        )}
        {field.type === 'select' && field.options && (
          <select
            value={(value as string) || field.options[0]?.value || ''}
            onChange={(e) => setValue(category, field.key, e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {field.options.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        )}
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold mb-2">Resource Limits</h1>
          <p className="text-muted-foreground">Configure default resource allocations for workspaces and apps</p>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 rounded-lg bg-destructive/10 text-destructive border border-destructive/20">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 rounded-lg bg-green-500/10 text-green-500 border border-green-500/20">
          {success}
        </div>
      )}

      <div className="space-y-4">
        {CATEGORIES.map((category) => {
          const Icon = category.icon
          const isExpanded = expandedCategories.has(category.key)

          return (
            <div key={category.key} className="border border-border rounded-xl bg-card overflow-hidden">
              <button
                onClick={() => toggleCategory(category.key)}
                className="w-full flex items-center justify-between p-4 hover:bg-secondary/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Icon className="w-5 h-5 text-primary" />
                  <div className="text-left">
                    <h3 className="font-semibold">{category.label}</h3>
                    <p className="text-sm text-muted-foreground">{category.description}</p>
                  </div>
                </div>
                {isExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
              </button>

              {isExpanded && (
                <div className="p-4 pt-0 border-t border-border">
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 pt-4">
                    {category.fields.map((field) => renderField(category.key, field))}
                  </div>

                  <div className="flex gap-3 mt-6 pt-4 border-t border-border">
                    <ConfirmDialog
                      trigger={
                        <button
                          disabled={saving === category.key}
                          className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border hover:bg-secondary transition-colors disabled:opacity-50"
                        >
                          <RotateCcw className="w-4 h-4" />
                          Reset to Defaults
                        </button>
                      }
                      title="Reset Resource Limits"
                      description={`Reset ${category.key === 'workspace_resources' ? 'workspace' : 'app'} resource limits to platform defaults? This cannot be undone.`}
                      confirmLabel="Reset"
                      variant="destructive"
                      onConfirm={() => handleReset(category.key)}
                    />
                    <button
                      onClick={() => handleSave(category.key)}
                      disabled={saving === category.key}
                      className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
                    >
                      <Save className="w-4 h-4" />
                      {saving === category.key ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
